// Route table. Two layouts: AuthLayout for /signin, AdminLayout for
// everything else.
//
// Admin IA (matches mockups/access-preview-v6.html):
//   Identity & Access
//     /admin/people                       Groups & users
//     /admin/people/groups/:groupId       Group detail (rules + members)
//     /admin/trace                        Effective access trace
//   Admin
//     /admin/roles                        Roles list
//     /admin/roles/new                    Custom role builder
//     /admin/roles/:roleId                Role detail
//     /admin/roles/:roleId/edit           Role editor
//     /admin/rules                        Tenant-wide rules table
//     /admin/rules/new                    Rule wizard (3 steps)
//     /admin/rules/:ruleId                Rule detail (group context)
//     /admin/rules/:ruleId/edit           Rule wizard pre-filled
//     /admin/locations                    Locations
//     /admin/audit                        Audit
//     /admin/tenant                       Workspace settings

import { Navigate, Route, Routes } from "react-router-dom";
import { AdminLayout } from "@/layouts/AdminLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { RequireAdmin } from "@/components/RequireAdmin";
import { CameraDetailPage } from "@/pages/CameraDetail";
import { CamerasPage } from "@/pages/Cameras";
import { DashboardPage } from "@/pages/Dashboard";
import { NotFoundPage } from "@/pages/NotFound";
import { RecordingsPage } from "@/pages/recordings/Recordings";
import { SignInPage } from "@/pages/SignIn";
import { ApiDocsPage } from "@/pages/admin/ApiDocs";
import { AdminAuditPage } from "@/pages/admin/Audit";
import { AdminAuditExportsPage } from "@/pages/admin/audit/Exports";
import { AdminAuditSubscriptionsPage } from "@/pages/admin/audit/Subscriptions";
import { RolesListPage } from "@/pages/admin/roles/RolesListPage";
import { RoleDetailPage } from "@/pages/admin/roles/RoleDetailPage";
import { RoleFormPage } from "@/pages/admin/roles/RoleFormPage";
import { RulesListPage } from "@/pages/admin/rules/RulesListPage";
import { RuleDetailPage } from "@/pages/admin/rules/RuleDetailPage";
import { RuleFormPage } from "@/pages/admin/rules/RuleFormPage";
import { PeoplePage } from "@/pages/admin/people/PeoplePage";
import { GroupDetailPage } from "@/pages/admin/people/GroupDetailPage";
import { UserFormPage } from "@/pages/admin/people/UserFormPage";
import { GroupFormPage } from "@/pages/admin/people/GroupFormPage";
import { TracePage } from "@/pages/admin/trace/TracePage";
// Ring App Store integration surfaces — pre-session and admin-gated.
import { TenantPickerPage } from "@/pages/TenantPicker";
import { WorkspaceSetupPage } from "@/pages/WorkspaceSetup";
import { RingLinkPage } from "@/pages/RingLink";
import { LinkErrorPage } from "@/pages/LinkError";
// /dashboards — v7 cameras dashboard surface (overview + per-dashboard wall).
import { DashboardsOverviewPage } from "@/pages/dashboards/DashboardsOverview";
import { DashboardWallPage } from "@/pages/dashboards/DashboardWall";
import { LocationsPage } from "@/pages/admin/Locations";
import { TenantSettingsPage } from "@/pages/admin/TenantSettings";

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* Auth surface — no topbar/sidebar. All pre-session (no auth required). */}
      <Route element={<AuthLayout />}>
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/tenant-picker" element={<TenantPickerPage />} />
        {/* Ring App Store install flow (Ring redirects the browser here before
            any session exists). /link replaces the former server-rendered
            /ringappstore/link form; /link-error renders the install error
            pages; /workspace-ready is the post-link landing (the callback set
            the session cookie before redirecting, so WorkspaceSetup polls
            /api/tenant until active). */}
        <Route path="/link" element={<RingLinkPage />} />
        <Route path="/link-error" element={<LinkErrorPage />} />
        <Route path="/workspace-ready" element={<WorkspaceSetupPage />} />
      </Route>

      {/* Admin surface — topbar + sidebar skeleton */}
      <Route element={<AdminLayout />}>
        {/* Camera surface — available to every role. */}
        <Route path="/cameras" element={<CamerasPage />} />
        <Route path="/cameras/:locationId" element={<CameraDetailPage />} />
        <Route path="/recordings" element={<RecordingsPage />} />

        <Route path="/dashboard" element={<DashboardPage />} />
        {/* v7 dashboards — overview list + per-dashboard wall surface. */}
        <Route path="/dashboards" element={<DashboardsOverviewPage />} />
        <Route
          path="/dashboards/:dashboardId"
          element={<DashboardWallPage />}
        />
        <Route path="/workspace-setup" element={<WorkspaceSetupPage />} />

        {/* Admin surface — fleet admin only. */}
        <Route element={<RequireAdmin />}>
          {/* Identity & Access */}
          <Route path="/admin/people" element={<PeoplePage />} />
          <Route path="/admin/people/users/new" element={<UserFormPage />} />
          <Route
            path="/admin/people/users/:alias/edit"
            element={<UserFormPage />}
          />
          <Route path="/admin/people/groups/new" element={<GroupFormPage />} />
          <Route
            path="/admin/people/groups/:groupId/edit"
            element={<GroupFormPage />}
          />
          <Route
            path="/admin/people/groups/:groupId"
            element={<GroupDetailPage />}
          />
          <Route path="/admin/trace" element={<TracePage />} />

          {/* Roles, rules — list + detail + form. */}
          <Route path="/admin/rules" element={<RulesListPage />} />
          <Route path="/admin/roles" element={<RolesListPage />} />
          <Route path="/admin/roles/new" element={<RoleFormPage />} />
          <Route path="/admin/roles/:roleId" element={<RoleDetailPage />} />
          <Route path="/admin/roles/:roleId/edit" element={<RoleFormPage />} />
          <Route path="/admin/rules/new" element={<RuleFormPage />} />
          <Route path="/admin/rules/:ruleId" element={<RuleDetailPage />} />
          <Route path="/admin/rules/:ruleId/edit" element={<RuleFormPage />} />
          <Route path="/admin/audit" element={<AdminAuditPage />} />
          <Route
            path="/admin/audit/exports"
            element={<AdminAuditExportsPage />}
          />
          <Route
            path="/admin/audit/subscriptions"
            element={<AdminAuditSubscriptionsPage />}
          />

          {/* Ring App Store integration admin surfaces. */}
          <Route path="/admin/locations" element={<LocationsPage />} />
          <Route path="/admin/tenant" element={<TenantSettingsPage />} />
        </Route>
        <Route path="/docs" element={<ApiDocsPage />} />
      </Route>

      {/* Root -> cameras; SessionProvider kicks unauth'd users to /signin. */}
      <Route path="/" element={<Navigate to="/cameras" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
